import { XMarkIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { ReactElement, useEffect, useState } from 'react'
import { useDrag } from 'react-dnd'
import { ItemTypes } from '../sortable-dnd/ItemTypes'
import { Tooltip } from '../Tooltip'
import { ContactSlotCard } from './ContactSlotCard'
import type { CommContext } from 'app/hooks/usePipelineCommContext'
import type { TaskCounts } from 'app/hooks/usePipelineTaskCounts'
import type { ListCardConfig } from 'lib/listCardConfig'
import type { ContactJson } from 'app/types/entities'

export function DraggableContact({
  contact,
  openContactDrawer,
  removeContact,
  config,
  taskCounts,
  commContext,
}: {
  contact: ContactJson
  openContactDrawer: (contactId: string) => void
  removeContact: (contactId: string) => void
  config: ListCardConfig
  taskCounts?: TaskCounts
  commContext?: CommContext
}): ReactElement {
  const [grabbingCursor, setGrabbingCursor] = useState(false)
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CONTACT,
    item: { id: contact.id!, pipelines: contact.pipelines },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  })

  useEffect(() => {
    if (isDragging) document.body.style.cursor = `grabbing`
    else {
      document.body.removeAttribute(`style`)
      setGrabbingCursor(false)
    }
  }, [isDragging])

  return (
    <div
      ref={(element) => {
        drag(element)
      }}
      className={classNames(
        `group relative z-20 rounded-lg border border-default`,
        isDragging ? `bg-surface-subtle` : `bg-surface`,
      )}
    >
      <Tooltip
        message="Remove contact from pipeline"
        className={`absolute right-0 hidden p-2 ${isDragging ? `` : `group-hover:block`}`}
      >
        <XMarkIcon
          className="cursor-pointer text-strong group-hover:h-4 group-hover:w-4"
          onClick={() => removeContact(contact.id!)}
        />
      </Tooltip>
      <a
        href={`/contacts?drawer=${contact?.id}`}
        className="no-underline hover:no-underline"
        onClick={(e) => {
          if (!(e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            openContactDrawer(contact.id!)
          }
        }}
        rel="noreferrer"
      >
        <ContactSlotCard
          contact={contact}
          config={config}
          taskCounts={taskCounts}
          commContext={commContext}
          dimmed={isDragging}
          cursor={grabbingCursor ? `grabbing` : `pointer`}
          onMouseDown={() => setGrabbingCursor(true)}
          onMouseUp={() => setGrabbingCursor(false)}
          onMouseLeave={() => setGrabbingCursor(false)}
        />
      </a>
    </div>
  )
}
